import { useSyncExternalStore } from 'react'

import type { Language } from '@/bindings/Language'
import type { UpdateProfileRequest } from '@/bindings/UpdateProfileRequest'
import { authPath, request } from '@/lib/api'
import { type System, type UserOf, getAuth, subscribeAuth, updateAuth } from '@/lib/auth-store'
import { initialLanguage, isLanguage, writeStoredLanguage } from '@/lib/i18n/languages'

let current: Language = initialLanguage()
const listeners = new Set<() => void>()

function set(language: Language) {
  if (language === current) return
  current = language
  if (typeof document !== 'undefined') document.documentElement.lang = language
  for (const l of listeners) l()
}

export function getLanguage(): Language {
  return current
}

export function subscribeLanguage(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useLanguage(): Language {
  return useSyncExternalStore(subscribeLanguage, getLanguage)
}

/** Switch the UI and remember it in this browser. */
export function setLanguage(language: Language) {
  writeStoredLanguage(language)
  set(language)
}

/**
 * The user's explicit choice: applies locally at once and, when logged in to `system`,
 * becomes the account preference (T4.7). The profile PUT clears absent fields, so the
 * current names are sent along.
 */
export async function chooseLanguage(language: Language, system?: System) {
  setLanguage(language)
  if (!system) return
  const auth = getAuth(system)
  if (auth.status !== 'authenticated' || auth.user.preferred_language === language) return
  const body: UpdateProfileRequest = {
    first_name: auth.user.first_name,
    last_name: auth.user.last_name,
    preferred_language: language,
  }
  const user = await request<UserOf<typeof system>>(authPath(system, '/profile'), { method: 'PUT', body })
  updateAuth(system, { user })
}

// Account preference wins whenever a session (re)appears with one — login, `me` on reload,
// or a profile edit. Keyed per user so a local switch afterward is not undone by unrelated
// auth updates. If both systems are logged in, platform is applied last and wins.
const applied: Partial<Record<System, string>> = {}

function syncFromAuth() {
  for (const system of ['restaurant', 'platform'] as const) {
    const auth = getAuth(system)
    if (auth.status !== 'authenticated') {
      delete applied[system]
      continue
    }
    const preferred = auth.user.preferred_language
    if (!isLanguage(preferred)) continue
    const key = `${auth.user.id}:${preferred}`
    if (applied[system] === key) continue
    applied[system] = key
    setLanguage(preferred)
  }
}

subscribeAuth(syncFromAuth)

/** Test-only: back to the first-visit state. */
export function resetLanguageForTests() {
  for (const system of ['restaurant', 'platform'] as const) delete applied[system]
  current = initialLanguage()
  if (typeof document !== 'undefined') document.documentElement.lang = current
  for (const l of listeners) l()
}
